/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { ICardFormProps } from '../types/CardHPC';
import { AdyenCheckout, Card } from '@adyen/adyen-web';
import '@adyen/adyen-web/styles/adyen.css';
import {
  Typography,
  AppHeaderBackButton,
  Box,
  Icon,
  Alert,
  AlertTypography,
  Modal,
  ModalTitle,
  ModalContent,
  ModalActions,
  ModalActionButton,
  ModalCloseIconButton,
  useNameOnCardValidator,
} from '@ace-clublabs/shared-component-library';
import { PaymentType, ErrorCode, IProcessPaymentWithManifestRequest } from '@ace-clublabs/payment-types';
import { useNavigate } from 'react-router-dom';
import { useGlobalState } from '../shared/state';
import PaymentHeader from './PaymentHeader';
import Header from './Header';
import '../styles/override.css';
import { sources } from '../api/sources';
import { IGetAdyenSession, ISubmitAdyenTokenRequest } from '../api/contracts';
import { Pages } from '../shared/pages';
import { ProcessPaymentHelper } from '../utils/processPaymentHelper';
import UrlHelper from '../utils/urlHelper';
import { ErrorSeverity, ErrorType } from '../shared/types';
import { UtagHelper } from '../utils/utagHelper';
import LogCannon from '@ace-clublabs/log-cannon';
import { PaymentReview } from '../containers/PaymentReview';
import { allowStoredPayment } from '@lib/shared';
import { CardFormValidationHelper } from '../utils/cardFormValidationHelper';
import cvvImage from '../assets/cvv.png';
import { Link, StickyButton } from '@ace-clublabs/shared-component-library';

// Local interfaces for Adyen session data
interface IAdyenEncryptedCardDataLocal {
  encryptedCardNumber: string;
  encryptedExpiryMonth: string;
  encryptedExpiryYear: string;
  encryptedSecurityCode?: string;
  holderName: string;
  postalCode: string;
}

interface IAdyenSessionData {
  sessionId: string;
  encryptedCardData: IAdyenEncryptedCardDataLocal;
}

/** Normalise a raw BIN string to a valid 6- or 8-digit BIN, or '' if unusable.
 *  < 6 → ''
 *  6–7 → first 6
 *  8+  → first 8
 */
// Single source of truth — pending product approval.
const UNSUPPORTED_CARD_MESSAGE = 'The card entered is not supported. Please try a different card.';

export const sanitizeBin = (raw: string): string => {
  if (raw.length < 6) return '';
  if (raw.length >= 8) return raw.substring(0, 8);
  return raw.substring(0, 6);
};

const CardFormAdyen: React.FC<ICardFormProps> = (props) => {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setProcessing] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showCvvModal, setShowCvvModal] = useState(false);
  const [adyenSession, setAdyenSession] = useState<IGetAdyenSession | null>(null);
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [isPaymentReview, setPaymentReview] = useState(false);
  const [, setAdyenCheckoutInstance] = useState<any>(null);
  const [cardComponent, setCardComponent] = useState<any>(null);
  const [adyenSessionData, setAdyenSessionData] = useState<IAdyenSessionData | null>(null);
  const [storePaymentChecked] = useState(false);
  const [adyenCardDetails, setAdyenCardDetails] = useState<{
    brand: string;
    last4: string;
    bin: string;
    brandImageUrl: string;
  }>({
    brand: '',
    last4: '',
    bin: '',
    brandImageUrl: '',
  });

  // Refs to prevent double initialization with React.StrictMode
  const adyenInitializedRef = useRef(false);
  const [securityCodeContainerEl, setSecurityCodeContainerEl] = useState<HTMLElement | null>(null);
  const [expiryDateContainerEl, setExpiryDateContainerEl] = useState<HTMLElement | null>(null);
  const latestAdyenOnChangeStateRef = useRef<any>(null);
  const nameOnCardValidator = useNameOnCardValidator({ maxLength: 26 });

  type RequiredFieldKey = 'holderName' | 'cardNumber' | 'expiryDate' | 'securityCode' | 'postalCode';

  const [requiredTouched, setRequiredTouched] = useState<Record<RequiredFieldKey, boolean>>({
    holderName: false,
    cardNumber: false,
    expiryDate: false,
    securityCode: false,
    postalCode: false,
  });

  const [requiredErrors, setRequiredErrors] = useState<Record<RequiredFieldKey, string | null>>({
    holderName: null,
    cardNumber: null,
    expiryDate: null,
    securityCode: null,
    postalCode: null,
  });

  const [fieldContainerEls, setFieldContainerEls] = useState<Record<RequiredFieldKey, HTMLElement | null>>({
    holderName: null,
    cardNumber: null,
    expiryDate: null,
    securityCode: null,
    postalCode: null,
  });

  const [_paymentType] = useGlobalState('paymentType') || [];
  const [manifest] = useGlobalState('manifest') || [];
  const [isMobileApp] = useGlobalState('isMobileApp') || [];
  const [paymentMethodsSkipped, setpaymentMethodsSkipped] = useGlobalState('paymentMethodsSkipped') || [];
  const [, setPaymentProcessing] = useGlobalState('paymentProcessing') || [];
  const [, setGlobalError] = useGlobalState('error');

  const {
    manifestId,
    paymentType,
    amount,
    returnUrl,
    autoPay,
    paymentFormSubtitle,
    setParentProcessing,
    isScheduled = false,
    softErrorCode,
    isUnitTest,
    appId,
    scheduledDate,
    tokenOnly,
  } = props;

  const config = manifest?.clientConfig || {};
  // const isScheduledPayment: boolean = !!isScheduled || !!manifest?.isScheduled;  //TODO not supported yet

  // Determine the payment type to use
  const pmtType =
    paymentType && paymentType !== PaymentType.Undefined
      ? paymentType
      : _paymentType && _paymentType !== PaymentType.Undefined
        ? _paymentType
        : PaymentType.Undefined;

  // Fetch Adyen session on mount
  useEffect(() => {
    let isMounted = true;

    const fetchAdyenSession = async () => {
      try {
        setIsLoadingSession(true);
        const sessionData = await sources.getAdyenSession(manifestId);

        if (isMounted) {
          setAdyenSession(sessionData);
        }
      } catch (error) {
        console.error('Failed to fetch Adyen session:', error);
        const errorMessage = 'ERROR: Failed to fetch Adyen session';
        LogCannon.blast(errorMessage, { error, manifestId });
        if (isMounted) {
          setError('Failed to load payment session. Please refresh to try again.');
          setIsLoadingSession(false);
          setParentProcessing({ status: false });
        }
      }
    };

    fetchAdyenSession();

    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [manifestId, amount]);

  const adyenSessionConfig = useMemo(() => {
    return adyenSession
      ? {
          id: adyenSession.adyen.sessionId,
          sessionData: adyenSession.adyen.sessionData,
        }
      : undefined;
  }, [adyenSession?.adyen.sessionId, adyenSession?.adyen.sessionData]);

  const handleSoftError = useCallback(
    (errorCode: ErrorCode) => {
      if (manifest?.tags) {
        const paymentTags = UtagHelper.tagPage(manifest.tags, 'payment');
        paymentTags['error'] = `blocked payment-${errorCode}`;
        UtagHelper.link(paymentTags);
      }
      switch (errorCode) {
        case ErrorCode.InvalidAccountHolderName:
          setError('Invalid name on the card.');
          break;
        case ErrorCode.InvalidExpirationDate:
          setError('Invalid expiration date.');
          break;
        case ErrorCode.InvalidCard:
        case ErrorCode.InvalidToken:
        case ErrorCode.InvalidInsuranceAutopayPaymentMethod:
        case ErrorCode.CardTypeNotAllowed:
        case ErrorCode.InvalidAccountNumber:
          setError('Invalid card number.');
          break;
        case ErrorCode.StorePaymentError:
          setError('Sorry, your payment method could not be saved for future payments.');
          break;
        case ErrorCode.UnknownCardType:
        case ErrorCode.StandaloneAutoPayError:
        case ErrorCode.InvalidPaymentGatewayToken:
          setError(UNSUPPORTED_CARD_MESSAGE);
          break;
        case ErrorCode.ChargeDeclined:
          setError('Charge declined, please use a different card.');
          break;
        case ErrorCode.CreditCardNotAllowed:
          setError('Only debit cards are accepted, please try again.');
          break;
        case ErrorCode.DebitCardNotAllowed:
          setError('Debit cards are not accepted, please try again.');
          break;
        case ErrorCode.PrepaidCardNotAllowedForAutoPay:
        case ErrorCode.PrepaidCardNotAllowed:
          setError('Prepaid cards are not accepted, please try again.');
          break;
        case ErrorCode.CardMustBeAAAVisa:
          setError('Card must be a AAA Visa card.');
          break;
        case ErrorCode.InvalidZipCode:
          setError('Invalid billing ZIP code.');
          break;
        case ErrorCode.InvalidCVV:
          setError('Invalid CVV/security code.');
          break;
        case ErrorCode.InsufficientFunds:
          setError('Insufficient funds, please try again.');
          break;
        case ErrorCode.NetworkTimeout:
          if (appId === 'manage-saved-payments') {
            setError('Your payment method could not be saved. Please try again.');
          } else {
            setError(null);
            setShowModal(true);
          }
          break;
        default:
          setError(null);
          setShowModal(true);
          break;
      }
    },
    [appId, manifest?.tags]
  );

  const validateRequiredField = useCallback(
    (field: RequiredFieldKey, state?: any): string | null => {
      const s = state ?? latestAdyenOnChangeStateRef.current;
      const pm = s?.data?.paymentMethod || {};
      const billing = s?.data?.billingAddress || {};

      switch (field) {
        case 'holderName':
          return pm?.holderName ? null : 'Name is required.';
        case 'cardNumber': {
          const hasEncrypted = !!pm?.encryptedCardNumber;
          const isCardNumberValid = s?.valid?.encryptedCardNumber === true;
          const errorCode = s?.errors?.encryptedCardNumber?.error || '';

          // Adyen has marked the field invalid — map error code to the right message
          if (!hasEncrypted && !isCardNumberValid) {
            if (errorCode === 'cc.num.903') return UNSUPPORTED_CARD_MESSAGE;
            if (errorCode === 'cc.num.901' || errorCode === 'cc.num.902') return 'Card number is not valid.';
            else return 'Card number is required.';
          }

          return null;
        }
        case 'expiryDate':
          // Check if Adyen has validation errors for expiry date
          const expiryDateError =
            s?.errors?.encryptedExpiryDate || s?.errors?.encryptedExpiryMonth || s?.errors?.encryptedExpiryYear;

          // If no encrypted data, it's required
          if ((!pm?.encryptedExpiryMonth || !pm?.encryptedExpiryYear) && !expiryDateError) {
            return 'Card expiration is required.';
          }

          return null;
        case 'securityCode':
          // CVV is not required when disableCVV is true
          if (config?.disableCVV) {
            return null;
          }
          return pm?.encryptedSecurityCode ? null : 'CVV/security code is required.';
        case 'postalCode':
          if (!billing?.postalCode) {
            return 'Billing ZIP code is required.';
          }
          if (billing.postalCode.length > 5) {
            return 'ZIP code must be 5 characters or less.';
          }
          return null;
        default:
          return null;
      }
    },
    [config.disableCVV]
  );

  const getFocusedRequiredFieldKey = useCallback((): RequiredFieldKey | null => {
    // Adyen secured fields live in iframes, so `document.activeElement` isn't helpful.
    // Adyen toggles `.adyen-checkout__label--focused` in the parent DOM; use it to infer "current field"
    const focusedLabel = document.querySelector('.adyen-checkout__label--focused') as HTMLElement | null;
    if (!focusedLabel) return null;

    // holderName is rendered in a dedicated container, not always a `__field--*`
    if (focusedLabel.closest('.adyen-checkout__card__holderName')) return 'holderName';

    const fieldEl = focusedLabel.closest('.adyen-checkout__field') as HTMLElement | null;
    if (!fieldEl) return null;

    if (fieldEl.classList.contains('adyen-checkout__field--cardNumber')) return 'cardNumber';
    if (fieldEl.classList.contains('adyen-checkout__field--expiryDate')) return 'expiryDate';
    if (fieldEl.classList.contains('adyen-checkout__field--securityCode')) return 'securityCode';
    if (
      fieldEl.classList.contains('adyen-checkout__field--postalCode') ||
      fieldEl.classList.contains('adyen-checkout__field--zipCode')
    )
      return 'postalCode';

    return null;
  }, []);

  const touchAndValidate = useCallback(
    (field: RequiredFieldKey) => {
      setRequiredTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));
      const error = validateRequiredField(field);
      setRequiredErrors((prev) => ({ ...prev, [field]: error }));
    },
    [validateRequiredField]
  );

  useEffect(() => {
    if (softErrorCode) {
      handleSoftError(softErrorCode);
    }
  }, [softErrorCode, handleSoftError]);

  const handlePaymentSuccess = (transactionId: string) => {
    if (setParentProcessing) {
      setParentProcessing({
        status: true,
        text: '',
        cindy: false,
        displayCheckmark: true,
        callback: () => {
          const returnUrlWithTransactionId = UrlHelper.addQueryParam(returnUrl, { transactionId });
          window.location.href = returnUrlWithTransactionId;
        },
      });
    }
  };

  const handleTokenOnlySubmit = async () => {
    if (!adyenSessionData || !adyenSessionData.encryptedCardData) {
      throw {
        type: ErrorSeverity.HARD,
        message: 'Encrypted card data not available for token submission.',
      };
    }

    const sessionId = adyenSessionData.sessionId;
    const payload: ISubmitAdyenTokenRequest = {
      adyen: {
        sessionId,
        cardData: {
          last4: adyenCardDetails.last4,
          cardBrand: adyenCardDetails.brand,
          cardHolderName: adyenSessionData.encryptedCardData.holderName,
          zipCode: adyenSessionData.encryptedCardData.postalCode,
          encryptedCardNumber: adyenSessionData.encryptedCardData.encryptedCardNumber,
          encryptedExpiryMonth: adyenSessionData.encryptedCardData.encryptedExpiryMonth,
          encryptedExpiryYear: adyenSessionData.encryptedCardData.encryptedExpiryYear,
          // Only include encryptedSecurityCode if it exists (when CVV is not disabled)
          ...(adyenSessionData.encryptedCardData.encryptedSecurityCode && {
            encryptedSecurityCode: adyenSessionData.encryptedCardData.encryptedSecurityCode,
          }),
          bin: adyenCardDetails.bin || 'credit',
        },
      },
    };

    LogCannon.blast('Submitting Adyen token', { sessionId, payload });

    const response = await sources.submitAdyenToken(sessionId, payload);

    LogCannon.blast('Adyen token submission successful', { response });

    setParentProcessing({
      status: true,
      text: '',
      cindy: false,
      displayCheckmark: true,
      callback: () => {
        window.location.href = returnUrl;
      },
    });
  };

  const handleVerifyAndContinue = async () => {
    setError(null);
    setParentProcessing({
      status: true,
      text: 'Verifying your card...',
      cindy: true,
      displayCheckmark: false,
    });
    setProcessing(true);

    try {
      // Validate the card using Adyen
      if (!cardComponent) {
        throw { type: ErrorSeverity.HARD, message: 'Card component not initialized' };
      }

      // Submit card state for validation
      const state = cardComponent.state;
      if (!state.isValid) {
        setParentProcessing({ status: false });
        setProcessing(false);
        handleSoftError(ErrorCode.InvalidCard);
        return;
      }

      // Encrypted card data should already be stored by onChange handler
      // Verify we have the data before proceeding
      if (!adyenSessionData || !adyenSessionData.encryptedCardData) {
        throw {
          type: ErrorSeverity.HARD,
          message: 'Encrypted card data not available. Please try again.',
        };
      }

      if (manifest?.clientConfig?.renderPaymentReview && amount > 0) {
        setParentProcessing({ status: false });
        setProcessing(false);
        setPaymentReview(true);
      } else if (tokenOnly) {
        await handleTokenOnlySubmit();
      } else {
        await submitPaymentFromReview({
          paymentDetails: {
            paymentType: pmtType,
            card: {
              last4: adyenCardDetails.last4,
              brand: adyenCardDetails.brand,
              bin: adyenCardDetails.bin,
            },
          },
          adyenSessionData,
        });
      }
    } catch (error: any) {
      LogCannon.blast('Adyen verification error', error);

      if (error?.type === ErrorSeverity.SOFT) {
        setParentProcessing({
          status: true,
          text: '',
          cindy: false,
          displayCheckmark: true,
          callback: () => {
            setProcessing(false);
          },
        });
        handleSoftError(error.code || ErrorCode.InvalidCard);
      } else {
        const errorMessage = 'ERROR: Failed to Verify Card';
        if (typeof error === 'string') {
          LogCannon.blast(`${errorMessage}: ${error}`);
        } else {
          LogCannon.blast(errorMessage, error as object);
        }
        navigate(Pages.Error);
      }
    }
  };

  const submitPaymentFromReview = async (state: any) => {
    const sessionData = state?.adyenSessionData || adyenSessionData;

    if (!sessionData || !sessionData.encryptedCardData) {
      const errorMessage = 'Submit payment from review is missing encrypted card data';
      LogCannon.blast(errorMessage);
      navigate(Pages.Error);
      return;
    }

    // Validate unsupported features
    if (isScheduled && scheduledDate) {
      const errorMessage = 'Scheduled payments not yet implemented for Adyen.';
      LogCannon.blast(errorMessage, { isScheduled, scheduledDate });
      navigate(Pages.Error);
      return;
    }

    if ((allowStoredPayment && storePaymentChecked) || appId === 'manage-saved-payments') {
      const errorMessage = 'Stored payments not yet implemented for Adyen.';
      LogCannon.blast(errorMessage, { storePaymentChecked, appId });
      navigate(Pages.Error);
      return;
    }

    if (tokenOnly) {
      await handleTokenOnlySubmit();
      return;
    }

    setError(null);
    setParentProcessing({
      status: true,
      text: '',
      cindy: false,
      displayCheckmark: false,
    });
    setProcessing(true);

    try {
      setPaymentProcessing(true);
      const processAdyenPaymentBody: IProcessPaymentWithManifestRequest = {
        manifestId: manifestId,
        paymentType: pmtType === PaymentType.Undefined ? PaymentType.CreditOrDebit : pmtType,
        adyenData: {
          sessionId: sessionData.sessionId, // For logging only
          cardData: {
            cardBrand: adyenCardDetails.brand,
            bin: adyenCardDetails.bin || 'credit',
            last4: adyenCardDetails.last4,
            cardHolderName: sessionData.encryptedCardData.holderName,
            zipCode: sessionData.encryptedCardData.postalCode,
            encryptedCardNumber: sessionData.encryptedCardData.encryptedCardNumber,
            encryptedExpiryMonth: sessionData.encryptedCardData.encryptedExpiryMonth,
            encryptedExpiryYear: sessionData.encryptedCardData.encryptedExpiryYear,
            // Only include encryptedSecurityCode if it exists (when CVV is not disabled)
            ...(sessionData.encryptedCardData.encryptedSecurityCode && {
              encryptedSecurityCode: sessionData.encryptedCardData.encryptedSecurityCode,
            }),
          },
        },
        scheduledDate: scheduledDate,
      };

      if (allowStoredPayment && storePaymentChecked) {
        processAdyenPaymentBody.storePayment = {
          enable: true,
        };
      }

      LogCannon.blast('Processing Adyen payment from review', processAdyenPaymentBody);

      const { transactionId } = await ProcessPaymentHelper.processPayment(processAdyenPaymentBody);

      handlePaymentSuccess(transactionId);
    } catch (error: any) {
      LogCannon.blast('Adyen payment error', error);

      if (error.type === ErrorSeverity.SOFT) {
        setParentProcessing({
          status: true,
          text: '',
          cindy: false,
          displayCheckmark: true,
          callback: () => {
            setProcessing(false);
            setPaymentReview(false);
          },
        });
        handleSoftError(error.code || ErrorCode.UnhandledError);
      } else {
        const errorMessage = 'ERROR: Failed to Process Adyen Payment From Review';
        if (typeof error === 'string') {
          LogCannon.blast(`${errorMessage}: ${error}`);
        } else {
          LogCannon.blast(errorMessage, error as object);
        }
        if (error.code === ErrorCode.TooManyRequests) {
          setGlobalError({ errorType: ErrorType.RATE_LIMITED, errorMessage: '' });
        }
        navigate(Pages.Error);
      }
    }
  };

  useEffect(() => {
    // Reset initialization ref when session config changes
    adyenInitializedRef.current = false;
  }, [adyenSessionConfig]);

  useEffect(() => {
    if (!adyenSessionConfig || !adyenSession) {
      return;
    }

    // Prevent double initialization with React.StrictMode in development
    if (adyenInitializedRef.current) {
      return;
    }
    adyenInitializedRef.current = true;

    let isMounted = true;
    let card: any = null;

    const initializeAdyen = async () => {
      try {
        if (!softErrorCode) {
          setError(null);
        }

        if (!isMounted) {
          return;
        }

        if (!adyenSession.adyen.clientKey) {
          throw new Error('Adyen clientKey is required but was not provided');
        }

        if (!adyenSession.adyen.sessionId || !adyenSession.adyen.sessionData) {
          throw new Error('Adyen sessionId or sessionData is missing from adyenSession');
        }

        let loadingHidden = false;

        const hideLoading = () => {
          if (!loadingHidden && isMounted) {
            loadingHidden = true;

            setTimeout(() => {
              if (isMounted) {
                setIsLoadingSession(false);
                setParentProcessing({ status: false });
              }
            }, 1000);
          }
        };
        const translations = {
          'creditCard.holderName': 'Name on the card',
          'creditCard.expiryDate.label': 'Expiration date',
          'creditCard.expiryDate.contextualText': 'MM/YY',
          'creditCard.securityCode.label': 'CVV/security code',
          'cc.num.900': 'Card number is required.',
          'cc.num.901': 'Card number must be at least 13 digits.',
          'cc.num.902': 'Card number is not valid.',
          'cc.num.903': UNSUPPORTED_CARD_MESSAGE,
          'cc.dat.912': 'Card expiration is not valid.',
          'cc.dat.911': 'Card expiration is not valid.',
          zipCode: 'Billing ZIP code',
          postalCode: 'Billing ZIP code',
          payButton: 'Continue',
          saveDetails: 'Continue',
          confirmPreauthorization: 'Continue',
        };
        const globalConfiguration = {
          session: adyenSessionConfig,
          environment: adyenSession.adyen.environment, // From backend API (test or live)
          amount: {
            value: props.amount,
            currency: 'USD',
          },
          locale: 'en-US',
          countryCode: 'US',
          clientKey: adyenSession?.adyen.clientKey,

          showPayButton: false, // We'll use our custom Continue button instead
          translations: {
            'en-US': {
              ...translations,
            },
            // Some Adyen Web setups use underscore locales (e.g. en_US). Keep both to be safe.
            en_US: {
              ...translations,
            },
            // Some environments/tools may pass a language-only locale (e.g. "en").
            en: {
              ...translations,
            },
          },

          onError: (error: any, component: any) => {
            const errorMessage = 'ERROR: Adyen Error';
            LogCannon.blast(errorMessage, { error, component });
            setParentProcessing({ status: false });
            setProcessing(false);
            navigate(Pages.Error);
          },
        };

        const cardConfiguration = {
          billingAddressRequired: true,
          billingAddressMode: 'partial' as const,
          billingAddressAllowedCountries: ['US'],
          hasHolderName: true,
          holderNameRequired: true,
          positionHolderNameOnTop: true,
          autoFocus: false,
          maskSecurityCode: true,
          showPayButton: false, // We'll use our custom Continue button instead
          // Hide CVV field when disableCVV is true
          hideCVC: !!config.disableCVV,
          amount: {
            value: 0,
            currency: 'USD',
          },
          onFieldValid: (event: any) => {
            // Only handle card number field - ignore other fields (CVV, expiry, etc.)
            if (event?.fieldType === 'encryptedCardNumber') {
              if (event.valid && event.endDigits) {
                // Capture endDigits from the card number field when valid
                setAdyenCardDetails((prev) => ({
                  ...prev,
                  last4: event.endDigits,
                }));
              } else if (!event.valid) {
                // Reset last4 when card number field becomes invalid
                setAdyenCardDetails((prev) => ({
                  ...prev,
                  last4: '',
                }));
              }

              // Handle bin from issuerBin (16+ digit cards only; not available for Amex)
              const fullBin = event.issuerBin ? event.issuerBin.toString() : '';

              if (event.valid && fullBin.length >= 6) {
                // Card complete and validated — upgrade to most precise BIN available
                const bin = sanitizeBin(fullBin);
                setAdyenCardDetails((prev) => {
                  return { ...prev, bin };
                });
              } else if (!event.valid) {
                // Card became invalid — clear the BIN
                setAdyenCardDetails((prev) => {
                  return { ...prev, bin: '' };
                });
                setError(null);
              } else {
                // Valid but no issuerBin (e.g. Amex 15-digit cards) — preserve captured BIN
                setAdyenCardDetails((prev) => {
                  return prev;
                });
              }
            }
          },
          onBinValue: (event: { binValue: string }) => {
            const candidate = event?.binValue || '';
            const newBin = sanitizeBin(candidate);
            if (!newBin) {
              // User is still typing (or deleting back) — clear the BIN
              setAdyenCardDetails((prev) => {
                return { ...prev, bin: '' };
              });
            } else {
              setAdyenCardDetails((prev) => {
                // If we already have an 8-digit confirmed BIN and the first 6 digits match,
                // the user is still on the same card — preserve the higher-precision 8-digit BIN.
                if (prev.bin.length === 8 && prev.bin.substring(0, 6) === newBin.substring(0, 6)) {
                  return prev;
                }
                // Otherwise take the sanitized BIN (new card or first time)
                return { ...prev, bin: newBin };
              });
            }
          },
          onBinLookup: (binLookupData: any) => {
            // Capture and map the card brand from supportedBrands
            const supportedBrands = binLookupData?.supportedBrands || [];
            if (supportedBrands.length > 0) {
              const adyenBrand = supportedBrands[0]; // Use the first supported brand
              const mappedBrand = CardFormValidationHelper.mapAdyenBrandToStandard(adyenBrand);

              // Update card details with mapped brand (preserve last4 and bin as-is)
              setAdyenCardDetails((prev) => {
                return {
                  brand: mappedBrand,
                  last4: prev.last4, // Keep existing last4 (empty or populated)
                  bin: prev.bin, // Keep existing bin (empty or populated)
                  brandImageUrl: prev.brandImageUrl,
                };
              });
            }
          },
          onChange: (state: any) => {
            hideLoading();
            latestAdyenOnChangeStateRef.current = state;

            // UX: if a field currently shows our "required" error, clear it as soon as the user starts typing.
            // Full required validation still runs on blur via `touchAndValidate`.
            // Exception: expiryDate validates in real-time, so don't clear its error here
            const focusedKey = getFocusedRequiredFieldKey();
            if (focusedKey && focusedKey !== 'expiryDate') {
              setRequiredErrors((prev) => (prev[focusedKey] ? { ...prev, [focusedKey]: null } : prev));
            }

            // Card number: re-evaluate the displayed error whenever Adyen sends updated state and
            // the field is not currently focused. Fixes the race where touchAndValidate runs before
            // Adyen's post-blur onChange arrives with the specific error code (cc.num.901/902/903).
            if (focusedKey !== 'cardNumber') {
              setRequiredErrors((prev) => {
                if (prev.cardNumber === null) return prev;
                return { ...prev, cardNumber: validateRequiredField('cardNumber', state) };
              });
            }

            // Real-time validation for expiry date field - validate while typing
            const hasExpiryInput = !!(
              state?.data?.paymentMethod?.encryptedExpiryMonth ||
              state?.data?.paymentMethod?.encryptedExpiryYear ||
              state?.valid?.encryptedExpiryDate === false ||
              state?.errors?.encryptedExpiryDate ||
              state?.errors?.encryptedExpiryMonth ||
              state?.errors?.encryptedExpiryYear
            );

            if (hasExpiryInput) {
              // Mark expiry date as touched so error displays immediately
              setRequiredTouched((prev) => ({ ...prev, expiryDate: true }));
              // Validate and set error
              const expiryError = validateRequiredField('expiryDate', state);
              setRequiredErrors((prev) => ({ ...prev, expiryDate: expiryError }));
            }

            const postalCode = state?.data?.billingAddress?.postalCode || '';
            const isValidUSZip = /^\d{5}$/.test(postalCode);
            const isZipTooLong = postalCode.length > 5;

            const holderName = state?.data?.paymentMethod?.holderName || '';

            const hasCardNumberInName = /\d{6,}/.test(holderName.replace(/[\s-]/g, ''));

            const isFormValidWithCustomRules =
              state.isValid && (postalCode === '' || isValidUSZip) && !hasCardNumberInName && !isZipTooLong;

            setIsFormValid(isFormValidWithCustomRules);

            // When form is valid, capture and store encrypted card data for payment submission
            if (state.isValid && state.data?.paymentMethod) {
              const paymentMethod = state.data.paymentMethod;
              const billingAddress = state.data.billingAddress || {};

              // Only store if we have encrypted data
              if (paymentMethod.encryptedCardNumber) {
                const encryptedCardData: IAdyenEncryptedCardDataLocal = {
                  encryptedCardNumber: paymentMethod.encryptedCardNumber,
                  encryptedExpiryMonth: paymentMethod.encryptedExpiryMonth,
                  encryptedExpiryYear: paymentMethod.encryptedExpiryYear,
                  holderName: paymentMethod.holderName || '',
                  postalCode: billingAddress.postalCode || '',
                };

                // Only include encryptedSecurityCode if it's present
                // When hideCVC is true, Adyen SDK won't provide encryptedSecurityCode
                if (paymentMethod.encryptedSecurityCode) {
                  encryptedCardData.encryptedSecurityCode = paymentMethod.encryptedSecurityCode;
                }

                setAdyenSessionData({
                  sessionId: adyenSessionConfig?.id || '',
                  encryptedCardData,
                });
              }
            }

            // Extract card brand and potentially last4 from the state
            if (state.data?.cardDetails) {
              setAdyenCardDetails((prev) => ({
                brand: state.data.cardDetails.type || prev.brand,
                last4: prev.last4, // Preserve last4 captured from onFieldValid or onBinValue
                bin: prev.bin, // Preserve bin captured from onBinValue
                brandImageUrl: prev.brandImageUrl,
              }));
            }
            // Update button state on change - disable if form invalid
            setTimeout(() => {
              const payButton = document.querySelector('.adyen-checkout__button') as HTMLButtonElement;
              if (payButton) {
                // If isUnitTest is true, don't disable the button (for testing purposes)
                const shouldDisable = !isFormValidWithCustomRules;
                payButton.disabled = isUnitTest ? false : shouldDisable;
                if (shouldDisable && !isUnitTest) {
                  payButton.setAttribute('data-form-invalid', 'true');
                } else {
                  payButton.removeAttribute('data-form-invalid');
                }
              }
            }, 0);

            // Re-validate required fields that have already been touched so errors clear as the user types.
            setRequiredErrors((prev) => {
              const next = { ...prev };
              (Object.keys(requiredTouched) as RequiredFieldKey[]).forEach((key) => {
                if (requiredTouched[key]) next[key] = validateRequiredField(key, state);
              });
              return next;
            });
          },
          brands: config.adyenCardBrands ?? ['amex', 'diners', 'discover', 'jcb', 'mc', 'visa'],
          styles: {
            base: {
              color: '#3D4043',
              fontSize: '16px',
              fontFamily: 'Roboto, Arial, sans-serif',
              fontWeight: '400',
            },
            error: {
              color: '#BE3317',
            },
          },
        };

        const checkout = await AdyenCheckout(globalConfiguration);

        // Check again if component is still mounted before mounting card
        if (!isMounted) {
          return;
        }

        card = new Card(checkout, cardConfiguration).mount('#card-container');

        // Store checkout and card instances for later use
        if (isMounted) {
          setAdyenCheckoutInstance(checkout);
          setCardComponent(card);
        }

        // Dismiss parent loading without waiting for the first onChange (Adyen may not emit until user input).
        hideLoading();

        // Note: No need to manage Adyen pay button since we're using custom Continue button
      } catch (error) {
        const errorMessage = 'ERROR: Failed to initialize Adyen card form';
        LogCannon.blast(errorMessage, { error, manifestId, appId });
        if (isMounted) {
          setParentProcessing?.({ status: false });
          navigate(Pages.Error);
        }
      }
    };

    initializeAdyen();

    // Cleanup function to prevent memory leaks and duplicate mounts
    return () => {
      isMounted = false;
      if (card) {
        try {
          card.unmount?.();
        } catch (e) {
          console.warn('Error unmounting Adyen card:', e);
        }
      }
    };
  }, [adyenSessionConfig, adyenSession, config.disableCVV]);

  useEffect(() => {
    if (!cardComponent) return;

    // Adyen renders this input asynchronously. We poll until it exists, then attach listeners.
    // On unmount, we stop polling and detach listeners.
    let isUnmounted = false;
    const timeouts: number[] = [];

    const attachWhenHolderNameInputReady = () => {
      if (isUnmounted) return;

      const holderInput = document.querySelector('.adyen-checkout__card__holderName input') as HTMLInputElement | null;
      if (!holderInput) {
        timeouts.push(window.setTimeout(attachWhenHolderNameInputReady, 50));
        return;
      }

      const onInputCapture = (e: Event) => {
        const target = e.target as HTMLInputElement | null;
        if (!target) return;
        const cleaned = nameOnCardValidator.validate({
          value: target.value,
          fieldProps: { label: 'Name on the card', required: true },
        }).value;
        if (cleaned !== target.value) {
          target.value = cleaned;
        }
      };

      // Block obvious invalid single-key entries early (paste/IME still handled by input sanitizer).
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        if (e.key.length !== 1) return;
        const allowed = /[a-zA-Z\s.'‘’\-]/.test(e.key);
        if (!allowed) e.preventDefault();
      };

      holderInput.addEventListener('input', onInputCapture, true);
      holderInput.addEventListener('keydown', onKeyDown);

      return () => {
        holderInput.removeEventListener('input', onInputCapture, true);
        holderInput.removeEventListener('keydown', onKeyDown);
      };
    };

    const cleanup = attachWhenHolderNameInputReady();

    return () => {
      isUnmounted = true;
      timeouts.forEach((t) => clearTimeout(t));
      if (typeof cleanup === 'function') cleanup();
    };
  }, [cardComponent, nameOnCardValidator]);

  const handleBack = () => {
    if (paymentMethodsSkipped) {
      setpaymentMethodsSkipped(false);
      navigate(-2);
    } else navigate(-1);
  };

  const cvvModalHandler = (e: any) => {
    e?.preventDefault?.();
    setShowCvvModal(true);
  };

  // Capture card brand image URL from Adyen's rendered icon
  useEffect(() => {
    if (!cardComponent) return;

    let cancelled = false;

    const captureImageUrl = () => {
      if (cancelled) return;

      const brandWrapper = document.querySelector(
        '.adyen-checkout__card__form .adyen-checkout__field--cardNumber .adyen-checkout-brand-wrapper'
      );
      if (!brandWrapper) {
        setTimeout(captureImageUrl, 100);
        return;
      }

      const brandImage = brandWrapper.querySelector('img.adyen-checkout__card__cardNumber__brandIcon');
      if (brandImage instanceof HTMLImageElement && brandImage.src) {
        setAdyenCardDetails((prev) => ({
          ...prev,
          brandImageUrl: brandImage.src,
        }));
      }
    };

    // Watch for image changes
    const intervalId = setInterval(() => {
      if (cancelled) return;

      const brandWrapper = document.querySelector(
        '.adyen-checkout__card__form .adyen-checkout__field--cardNumber .adyen-checkout-brand-wrapper'
      );
      if (brandWrapper) {
        const brandImage = brandWrapper.querySelector('img.adyen-checkout__card__cardNumber__brandIcon');
        if (brandImage instanceof HTMLImageElement && brandImage.src) {
          setAdyenCardDetails((prev) => {
            if (prev.brandImageUrl !== brandImage.src) {
              return { ...prev, brandImageUrl: brandImage.src };
            }
            return prev;
          });
        }
      }
    }, 500);

    captureImageUrl();

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [cardComponent]);

  // Mount the CVV "Where do I find this?" link directly under the Adyen CVV field
  // by portaling into the security code field container once it exists in the DOM.
  useEffect(() => {
    if (!cardComponent) return;

    let cancelled = false;
    const tryFind = () => {
      if (cancelled) return;
      const el = document.querySelector('.adyen-checkout__field--securityCode') as HTMLElement | null;
      if (el) {
        setSecurityCodeContainerEl(el);
        return;
      }
      // Adyen renders async; retry shortly.
      setTimeout(tryFind, 50);
    };

    tryFind();
    return () => {
      cancelled = true;
      setSecurityCodeContainerEl(null);
    };
  }, [cardComponent]);

  // Mount the expiry date error message container - create a div after input wrapper
  useEffect(() => {
    if (!cardComponent) return;

    let cancelled = false;
    const tryFind = () => {
      if (cancelled) return;
      const field = document.querySelector('.adyen-checkout__field--expiryDate') as HTMLElement | null;
      if (!field) {
        setTimeout(tryFind, 50);
        return;
      }

      // Check if we already created the error container
      let errorContainer = field.querySelector('.expiry-error-container') as HTMLElement | null;

      if (!errorContainer) {
        // Create a container div for the error
        errorContainer = document.createElement('div');
        errorContainer.className = 'expiry-error-container';

        // Insert it after the input wrapper
        const inputWrapper = field.querySelector('.adyen-checkout__input-wrapper');
        if (inputWrapper && inputWrapper.parentNode) {
          inputWrapper.parentNode.insertBefore(errorContainer, inputWrapper.nextSibling);
        }
      }

      setExpiryDateContainerEl(errorContainer);
    };

    tryFind();
    return () => {
      cancelled = true;
      setExpiryDateContainerEl(null);
    };
  }, [cardComponent]);

  // Add focus listener to CVV link to trigger validation when tabbing from CVV input to link
  useEffect(() => {
    if (!cardComponent) return;

    let cancelled = false;
    const timeouts: number[] = [];

    const attachLinkFocusListener = () => {
      if (cancelled) return;
      const cvvLink = document.getElementById('cvv-desc-link');
      if (!cvvLink) {
        timeouts.push(window.setTimeout(attachLinkFocusListener, 50));
        return;
      }

      const onLinkFocus = () => {
        if (cancelled) return;
        // Trigger CVV validation when the link receives focus
        touchAndValidate('securityCode');
      };

      cvvLink.addEventListener('focus', onLinkFocus);

      return () => {
        cvvLink.removeEventListener('focus', onLinkFocus);
      };
    };

    const cleanup = attachLinkFocusListener();

    return () => {
      cancelled = true;
      timeouts.forEach((t) => clearTimeout(t));
      if (typeof cleanup === 'function') cleanup();
    };
  }, [cardComponent, touchAndValidate]);

  // Required field validation on blur (show error when user focuses a field then leaves it empty).
  useEffect(() => {
    if (!cardComponent) return;

    let cancelled = false;
    const timeouts: number[] = [];
    const cleanups: Array<() => void> = [];

    const setContainer = (key: RequiredFieldKey, el: HTMLElement | null) => {
      setFieldContainerEls((prev) => (prev[key] === el ? prev : { ...prev, [key]: el }));
    };

    // Track which fields were previously focused
    const previouslyFocused = new Set<RequiredFieldKey>();

    // Use MutationObserver to watch for Adyen focus class changes
    const setupFocusObserver = () => {
      const observer = new MutationObserver((mutations) => {
        if (cancelled) return;

        // Check if any mutation affected a label with the focused class
        const hasFocusClassChange = mutations.some((mutation) => {
          const target = mutation.target as HTMLElement;
          return target.classList?.contains('adyen-checkout__label') || target.closest?.('.adyen-checkout__label');
        });

        if (!hasFocusClassChange) return;

        // Check which field is currently focused
        const currentlyFocusedKey = getFocusedRequiredFieldKey();

        // For each previously focused field that is no longer focused, validate it
        previouslyFocused.forEach((key) => {
          if (key !== currentlyFocusedKey) {
            // Field lost focus, validate it
            touchAndValidate(key);
            previouslyFocused.delete(key);
          }
        });

        // Track the newly focused field
        if (currentlyFocusedKey && !previouslyFocused.has(currentlyFocusedKey)) {
          previouslyFocused.add(currentlyFocusedKey);
        }
      });

      // Observe the entire card container for class changes
      const cardContainer = document.getElementById('card-container');
      if (cardContainer) {
        observer.observe(cardContainer, {
          attributes: true,
          attributeFilter: ['class'],
          subtree: true,
        });
        cleanups.push(() => observer.disconnect());
      }
    };

    const attachFocusOut = (key: RequiredFieldKey, el: HTMLElement | null) => {
      if (!el) return;

      const onFocusOut = () => {
        if (cancelled) return;

        // Focus transitions inside a field (e.g. expiry month -> expiry year) should NOT trigger validation.
        // Defer a tick, then validate only if focus ended up outside this field container.
        const t = window.setTimeout(() => {
          if (cancelled) return;
          const active = document.activeElement as HTMLElement | null;
          if (active && el.contains(active)) {
            // If focus stayed within the field's actual input wrapper (secured field iframe area),
            // treat it as an internal focus transition (don't validate yet).
            // But if focus moved to *other* content inside the container (e.g. "Where do I find this?" link),
            // treat it like leaving the field and validate immediately.
            const activeWrapper = active.closest?.('.adyen-checkout__input-wrapper') as HTMLElement | null;
            if (activeWrapper && el.contains(activeWrapper)) return;

            // Special case for CVV: don't validate if focus moved to the CVV link (handled by link focus event)
            if (key === 'securityCode') {
              const isCvvLink = active?.id === 'cvv-desc-link' || active?.closest('#cvv-desc-link');
              if (isCvvLink) return;
            }
          }
          touchAndValidate(key);
        }, 0);
        timeouts.push(t);
      };

      // focusout bubbles; this catches both normal inputs and iframe focus changes (document.activeElement becomes the iframe).
      el.addEventListener('focusout', onFocusOut);
      cleanups.push(() => el.removeEventListener('focusout', onFocusOut));
    };

    const tryFind = () => {
      if (cancelled) return;

      const holderName = document.querySelector('.adyen-checkout__card__holderName') as HTMLElement | null;
      const cardNumber =
        (document.querySelector('.adyen-checkout__field--cardNumber') as HTMLElement | null) ||
        (document.querySelector('.adyen-checkout__card__cardNumber') as HTMLElement | null);
      const expiryDate =
        (document.querySelector('.adyen-checkout__field--expiryDate') as HTMLElement | null) ||
        (document.querySelector('.adyen-checkout__card__expiryDate') as HTMLElement | null);
      const securityCode = document.querySelector('.adyen-checkout__field--securityCode') as HTMLElement | null;
      const postalCode =
        (document.querySelector('.adyen-checkout__field--postalCode') as HTMLElement | null) ||
        (document.querySelector('.adyen-checkout__field--zipCode') as HTMLElement | null);

      setContainer('holderName', holderName);
      setContainer('cardNumber', cardNumber);
      setContainer('expiryDate', expiryDate);
      setContainer('securityCode', securityCode);
      setContainer('postalCode', postalCode);

      // When CVV is disabled (hideCVC), Adyen does not render securityCode - don't require it.
      const cvvDisabled = config?.disableCVV === true;
      const hasRequiredFields = cvvDisabled
        ? holderName && cardNumber && expiryDate && postalCode
        : holderName && cardNumber && expiryDate && securityCode && postalCode;

      if (hasRequiredFields) {
        attachFocusOut('holderName', holderName);
        attachFocusOut('cardNumber', cardNumber);
        attachFocusOut('expiryDate', expiryDate);
        if (securityCode) attachFocusOut('securityCode', securityCode);
        attachFocusOut('postalCode', postalCode);

        // Also setup the MutationObserver for Adyen focus class changes
        setupFocusObserver();
        return;
      }

      timeouts.push(window.setTimeout(tryFind, 50));
    };

    tryFind();

    return () => {
      cancelled = true;
      timeouts.forEach((t) => clearTimeout(t));
      cleanups.forEach((fn) => fn());
    };
  }, [cardComponent, touchAndValidate, getFocusedRequiredFieldKey, config?.disableCVV]);

  // Apply a CSS class to field containers so required-on-blur errors show a red border like other Adyen errors.
  useEffect(() => {
    (Object.keys(fieldContainerEls) as RequiredFieldKey[]).forEach((key) => {
      const el = fieldContainerEls[key];
      if (!el) return;
      if (requiredTouched[key] && requiredErrors[key]) el.classList.add('fp-required-error');
      else el.classList.remove('fp-required-error');
    });
  }, [fieldContainerEls, requiredErrors, requiredTouched]);

  const modalDialog = (
    <Modal
      open={showModal}
      onClose={() => setShowModal(false)}
      variants={{
        sm: 'bottom-sheet',
        md: 'regular',
      }}
    >
      <ModalTitle id={`error-modal-title`} sx={{ marginRight: '2rem' }}>
        Please try again.
        <ModalCloseIconButton aria-label="Close" onClick={() => setShowModal(false)} />
      </ModalTitle>

      <ModalContent id="error-modal-content">
        <Typography variant="b1Regular">There was an unexpected error.</Typography>
      </ModalContent>
      <ModalActions
        actionLayout={{
          xs: 'fullWidth',
          sm: 'fullWidth',
          md: 'fullWidth',
          lg: 'rightAlign',
          xl: 'rightAlign',
        }}
      >
        <ModalActionButton label="OK" onClick={() => setShowModal(false)} />
      </ModalActions>
    </Modal>
  );

  const cvvModal = (
    <Modal
      open={showCvvModal}
      onClose={() => setShowCvvModal(false)}
      variants={{
        sm: 'bottom-sheet',
        md: 'regular',
      }}
    >
      <ModalTitle id={`modal-title-cvv`} sx={{ marginRight: '2rem' }}>
        Where do I find this?
        <ModalCloseIconButton aria-label="Close" onClick={() => setShowCvvModal(false)} />
      </ModalTitle>

      <ModalContent id="modal-content-cvv">
        <Typography variant="b1Regular">
          The CVV or Card Verification Value is a 3-digit code usually found on the back of Visa or Mastercard credit
          and debit cards. For American Express cards, it is a 4-digit code on the front of your card.
        </Typography>
        <div className="cvv-img-div">
          <img src={cvvImage} className="cvv-img" alt="cvv" />
        </div>
      </ModalContent>
      <ModalActions
        actionLayout={{
          sm: 'fullWidth',
          md: 'fullWidth',
          lg: 'rightAlign',
          xl: 'rightAlign',
        }}
      >
        <ModalActionButton label="Got it" onClick={() => setShowCvvModal(false)} />
      </ModalActions>
    </Modal>
  );

  const amountDue = amount > 0;
  const renderPaymentReview = manifest?.clientConfig?.renderPaymentReview;
  const buttonText = !isScheduled && amountDue && !renderPaymentReview ? 'Pay now' : 'Continue';
  const disabled = isUnitTest ? false : !isFormValid;

  return (
    <>
      {/* Debug Info */}
      <div style={{ padding: '10px', backgroundColor: '#f0f0f0', margin: '10px', fontSize: '12px', display: 'none' }}>
        isLoading: {String(isLoadingSession)} | hasSession: {String(!!adyenSession)} | hasConfig:{' '}
        {String(!!adyenSessionConfig)} | initialized: {String(adyenInitializedRef.current)}
      </div>
      <div
        style={isLoadingSession || isProcessing ? { visibility: 'hidden', position: 'absolute', left: '-9999px' } : {}}
      >
        {isPaymentReview && (
          <PaymentReview
            amount={manifest?.amount!}
            paymentDetails={{
              paymentType: PaymentType.Undefined,
              card: {
                last4: adyenCardDetails.last4 || '',
                brand: adyenCardDetails.brand || '',
                bin: adyenCardDetails.bin || 'credit',
                brandImageUrl: adyenCardDetails.brandImageUrl || '',
              },
            }}
            adyenSessionData={adyenSessionData || undefined}
            pit={manifest?.pit ? { ...manifest.pit } : undefined}
            scheduledDate={scheduledDate}
            onSubmit={submitPaymentFromReview}
            onBack={() => {
              setPaymentReview(false);
              setParentProcessing({ status: false });
            }}
          />
        )}

        <Box className={`payment-form ${!isPaymentReview && !isProcessing ? '' : 'shrink'}`} tabIndex={0}>
          <Box className="payment-form-cream-section">
            <Box className="payment-form-body inner-container">
              <Header />
              {!isMobileApp && <AppHeaderBackButton id="back-button" onClick={handleBack} />}
              <Box className="secure-checkout">
                <Icon iconName="lock-keyhole-solid" className="lock-icon" />
                <span>Secure checkout</span>
              </Box>
              <PaymentHeader paymentType={pmtType} amount={amount} autoPay={autoPay} />
              {paymentFormSubtitle ? (
                <Typography variant="b1Regular" data-quid="card-form-subtitle">
                  {paymentFormSubtitle}
                </Typography>
              ) : (
                <Box id="card-form-subtitle-container">
                  {config?.allowedPaymentTypes?.includes(PaymentType.DebitOnly) && (
                    <Box className="card-subtitle" id="payment-method-debit-card">
                      <Typography variant="b1Regular" data-quid="card-form-subtitle">
                        Debit card
                      </Typography>
                      <Box className="card-logo" id="debit-card-logos">
                        <Icon iconName="mastercard-logo" className="card-header-icons" />
                        <Icon iconName="visa-logo" className="card-header-icons" />
                      </Box>
                    </Box>
                  )}
                  {config?.allowedPaymentTypes?.includes(PaymentType.CreditOrDebit) && (
                    <Box
                      className="card-subtitle"
                      id="payment-method-credit-card"
                      sx={{ alignContent: 'space-between' }}
                    >
                      <Typography
                        variant="b1Regular"
                        data-quid="card-form-subtitle"
                        sx={{ color: '#3D4043 !important' }}
                      >
                        Credit card
                      </Typography>
                      <Box className="card-logo" id="credit-card-logos">
                        <Icon iconName="amex-logo" className="card-header-icons" />
                        <Icon iconName="mastercard-logo" className="card-header-icons" />
                        <Icon iconName="visa-logo" className="card-header-icons" />
                        <Icon iconName="discover-logo" className="card-header-icons" />
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </Box>
          </Box>
          <Box className="payment-form-body inner-container">
            {error && (
              <Alert severity="error" id="soft-error-alert" displayCloseButton={false}>
                <AlertTypography>{error}</AlertTypography>
              </Alert>
            )}
            <div id="card-container"></div>
            {/* Required-on-blur field errors (rendered into Adyen DOM containers) */}
            {fieldContainerEls.holderName &&
              requiredTouched.holderName &&
              requiredErrors.holderName &&
              createPortal(
                <div className="adyen-checkout-contextual-text adyen-checkout-contextual-text--error">
                  {requiredErrors.holderName}
                </div>,
                fieldContainerEls.holderName
              )}
            {fieldContainerEls.cardNumber &&
              requiredTouched.cardNumber &&
              requiredErrors.cardNumber &&
              createPortal(
                <div
                  className="adyen-checkout-contextual-text adyen-checkout-contextual-text--error"
                  data-custom-error="true"
                >
                  {requiredErrors.cardNumber}
                </div>,
                fieldContainerEls.cardNumber
              )}
            {expiryDateContainerEl &&
              requiredTouched.expiryDate &&
              requiredErrors.expiryDate &&
              createPortal(
                <div
                  className="adyen-checkout-contextual-text adyen-checkout-contextual-text--error"
                  data-custom-error="true"
                  style={{ order: -1 }}
                >
                  {requiredErrors.expiryDate}
                </div>,
                expiryDateContainerEl
              )}
            {securityCodeContainerEl &&
              createPortal(
                <>
                  {requiredTouched.securityCode && requiredErrors.securityCode && (
                    <div
                      className="adyen-checkout-contextual-text adyen-checkout-contextual-text--error"
                      data-custom-error="true"
                    >
                      {requiredErrors.securityCode}
                    </div>
                  )}
                  <div style={{ marginTop: '0', display: 'block' }}>
                    <span style={{ display: 'inline-block' }}>
                      <Link component="button" id="cvv-desc-link" className="secondary" onClick={cvvModalHandler}>
                        Where do I find this?
                      </Link>
                    </span>
                  </div>
                </>,
                securityCodeContainerEl
              )}
            {fieldContainerEls.postalCode &&
              requiredTouched.postalCode &&
              requiredErrors.postalCode &&
              createPortal(
                <div className="adyen-checkout-contextual-text adyen-checkout-contextual-text--error">
                  {requiredErrors.postalCode}
                </div>,
                fieldContainerEls.postalCode
              )}
          </Box>
          <StickyButton
            id="cc-submit"
            slotProps={{
              primaryButton: {
                label: buttonText,
                onClick: handleVerifyAndContinue,
                disabled,
                endIcon: amountDue ? <Icon iconName="lock-keyhole-solid" /> : undefined,
              },
            }}
            renderAmount={amountDue ? amount : undefined}
          />
        </Box>
        {modalDialog}
        {cvvModal}
      </div>
    </>
  );
};

export default CardFormAdyen;
